import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Departures, SalesTypes } from 'app/core/enums/common.enum';
import { FinanceResources } from 'app/core/security/resources/resources';
import { VenueFinancesService } from 'app/modules/sho_management_pro/venue/services/venue-finances.service';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { AdapterService } from '../../services/adapters/adapter.service';
import { ParkingSalesService } from '../../services/sales/parking/parking-sales.service';
import { TotalGrossService } from '../../services/sales/total-gross.service';
import * as ParkingSalesActions from './parking-sales.actions';

@Injectable()
export class ParkingSalesEffects {
    private _actions$ = inject(Actions);
    private _saleService = inject(ParkingSalesService);
    private _alertService = inject(AlertService);
    private _venueFinancesService = inject(VenueFinancesService);
    private _totalGrossService = inject(TotalGrossService);
    private adapter = inject(AdapterService);

    messages = {
        getAllError: 'Error getting all Parking Sales',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Parking Sales
    getAllParkingSales$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ParkingSalesActions.loadParkingSales),
            switchMap((action) => {
                return this._saleService.getAll(action.playdateId).pipe(
                    map((res) => {
                        const data = this.adapter.adaptPkOtSalesList(
                            res.data,
                            FinanceResources.PARKING
                        );

                        return ParkingSalesActions.loadParkingSalesSuccess({
                            data: data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            ParkingSalesActions.loadParkingSalesFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create Parking Sale
    createParkingSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ParkingSalesActions.createParkingSale),
            switchMap((action) => {
                return this._saleService.create(action.data).pipe(
                    map((res) => {
                        const sale = this.adapter.adaptPkOtSale(
                            res.data,
                            FinanceResources.PARKING
                        );
                        return ParkingSalesActions.createParkingSaleSuccess({
                            sale: sale,
                            success: 'Parking Sale created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            ParkingSalesActions.createParkingSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update Parking Sale
    updateParkingSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ParkingSalesActions.updateParkingSale),
            switchMap((action) => {
                return this._saleService.update(action.data, action.id).pipe(
                    map((res) => {
                        const sale = this.adapter.adaptPkOtSale(
                            res.data,
                            FinanceResources.PARKING
                        );
                        return ParkingSalesActions.updateParkingSaleSuccess({
                            data: sale,
                            success: 'Parking Sale updated successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            ParkingSalesActions.updateParkingSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Delete Sale
    deleteParkingSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ParkingSalesActions.deleteParkingSale),
            switchMap((action) => {
                return this._saleService.delete(action.payload).pipe(
                    map((res) => {
                        const data = res.data;
                        return ParkingSalesActions.deleteParkingSaleSuccess({
                            deletedSales: data.deleted_sales,
                            remainingSales: data.remaining_sales,
                            success: 'Parking Sale deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            ParkingSalesActions.deleteParkingSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Delete Sale Item
    deleteParkingSaleItem$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ParkingSalesActions.deleteParkingSaleItem),
            switchMap((action) => {
                return this._saleService.delete(action.payload).pipe(
                    map((res) => {
                        const saleId = Number(res.data.deleted_sales[0]);
                        const totals = res.data.totals;

                        if (!saleId) {
                            const message =
                                'Sale item ID not found in response';
                            this._alertService.showAlert(message, 'error');
                            throw new Error(message);
                        }

                        return ParkingSalesActions.deleteParkingSaleItemSuccess(
                            {
                                deletedSaleItem: saleId,
                                totals,
                                success:
                                    'Parking sale item deleted successfully',
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            ParkingSalesActions.deleteParkingSaleItemFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onParkingSaleActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    ParkingSalesActions.createParkingSaleSuccess,
                    ParkingSalesActions.updateParkingSaleSuccess,
                    ParkingSalesActions.deleteParkingSaleSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');

                    this._venueFinancesService.onUpdateFinances = true;
                    this._totalGrossService.onValidateFinances(
                        Departures.PARKING,
                        SalesTypes.SALE_PARKING,
                        FinanceResources.PARKING
                    );
                })
            ),
        { dispatch: false }
    );

    onParkingSaleActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    ParkingSalesActions.loadParkingSalesFailure,
                    ParkingSalesActions.loadParkingSaleFailure,
                    ParkingSalesActions.createParkingSaleFailure,
                    ParkingSalesActions.updateParkingSaleFailure,
                    ParkingSalesActions.deleteParkingSaleFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
